import React, { useState, useEffect } from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import { Close, Title, AutoAwesome, WorkspacePremium } from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { useTheme } from '@mui/material/styles';
import notificationSound from '../../services/notificationSoundService';
import SwipeableNotification from '../Notifications/SwipeableNotification';

const PremiumTitleNotification = ({ 
  titleData, 
  onClose, 
  show = true 
}) => {
  const theme = useTheme();
  const [visible, setVisible] = useState(false);
  const [particles, setParticles] = useState([]);

  useEffect(() => {
    if (show) {
      setVisible(true);
      notificationSound.play('title');
      const newParticles = Array.from({ length: 15 }, (_, i) => ({
        id: i,
        x: Math.random() * 400,
        y: Math.random() * 300,
        delay: Math.random() * 2,
        duration: 2 + Math.random() * 2
      }));
      setParticles(newParticles);

      const timer = setTimeout(() => {
        handleClose();
      }, 6000);

      return () => clearTimeout(timer);
    } else {
      setVisible(false);
    }
  }, [show]);

  const handleClose = () => {
    setVisible(false);
    setTimeout(() => onClose?.(), 300);
  };

  if (!titleData) return null;

  const isDark = theme.palette.mode === 'dark';
  
  const titleColors = {
    primary: isDark ? '#FFD700' : '#FF8F00',
    secondary: isDark ? '#FFA500' : '#FFB300',
    glow: isDark ? 'rgba(255, 215, 0, 0.4)' : 'rgba(255, 143, 0, 0.3)'
  };
  
  const containerStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 9999,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
    backdropFilter: 'blur(10px)',
  };

  const cardStyle = {
    position: 'relative',
    width: '400px',
    maxWidth: '90vw',
    padding: '32px',
    borderRadius: '24px',
    background: isDark 
      ? `linear-gradient(135deg, ${titleColors.primary}20 0%, ${titleColors.secondary}10 50%, rgba(0, 0, 0, 0.9) 100%)`
      : 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 255, 0.9) 100%)',
    border: `2px solid ${titleColors.primary}50`,
    boxShadow: `0 20px 40px ${titleColors.glow}, 0 0 60px ${titleColors.primary}20`,
    overflow: 'hidden',
  };

  return (
    <AnimatePresence>
      {show && visible && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          style={containerStyle}
        >
          {/* Celebration Particles */}
          {particles.map((particle) => (
            <motion.div
              key={particle.id}
              initial={{ 
                opacity: 0, 
                scale: 0,
                x: particle.x,
                y: particle.y,
                rotate: 0
              }}
              animate={{ 
                opacity: [0, 1, 0], 
                scale: [0, 1, 0],
                y: particle.y - 100,
                rotate: 360
              }}
              transition={{ 
                duration: particle.duration,
                delay: particle.delay,
                ease: "easeOut"
              }}
              style={{
                position: 'absolute',
                width: '8px',
                height: '8px',
                background: titleColors.primary,
                borderRadius: '50%',
              }}
            />
          ))}

          <SwipeableNotification onDismiss={handleClose} showCloseButton={false}>
            <motion.div
              initial={{ scale: 0, rotate: -180 }}
              animate={{ scale: 1, rotate: 0 }}
              exit={{ scale: 0, rotate: 180 }}
              transition={{ 
                type: "spring", 
                stiffness: 200, 
                damping: 20,
                duration: 0.6
              }}
              style={cardStyle}
            >
              {/* Close Button */}
              <IconButton
                onClick={handleClose}
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  color: `${titleColors.primary}B3`,
                  '&:hover': {
                    color: titleColors.primary,
                  }
                }}
              >
                <Close />
              </IconButton>

            {/* Title Icon */}
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              mb: 2,
              position: 'relative'
            }}>
              <motion.div
                animate={{ 
                  rotate: [0, 10, -10, 0],
                  scale: [1, 1.1, 1]
                }}
                transition={{ 
                  duration: 2,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
              >
                <Box sx={{
                  width: 80,
                  height: 80,
                  borderRadius: '50%',
                  background: `linear-gradient(135deg, ${titleColors.primary} 0%, ${titleColors.secondary} 100%)`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: `0 8px 32px ${titleColors.glow}`,
                }}>
                  <WorkspacePremium sx={{ 
                    fontSize: 40, 
                    color: 'white',
                  }} />
                </Box>
              </motion.div>
              
              {/* Floating Stars */}
              {[...Array(3)].map((_, i) => (
                <motion.div
                  key={i}
                  animate={{
                    y: [-10, -20, -10],
                    opacity: [0.5, 1, 0.5],
                    scale: [0.8, 1.2, 0.8]
                  }}
                  transition={{
                    duration: 2,
                    delay: i * 0.3,
                    repeat: Infinity,
                    ease: "easeInOut"
                  }}
                  style={{
                    position: 'absolute',
                    top: `${20 + i * 15}px`,
                    left: `${100 + i * 30}px`,
                  }}
                >
                  <AutoAwesome sx={{ 
                    color: titleColors.primary,
                    fontSize: 16 + i * 2
                  }} />
                </motion.div>
              ))}
            </Box>

            {/* Title Update Message */}
            <Typography
              variant="h4"
              sx={{
                textAlign: 'center',
                fontWeight: 'bold',
                background: `linear-gradient(135deg, ${titleColors.primary} 0%, ${titleColors.secondary} 100%)`,
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                mb: 1,
                textShadow: `0 2px 10px ${titleColors.glow}`,
              }}
            >
              Title Updated!
            </Typography>

            {/* New Title Name */}
            <Typography
              variant="h6"
              sx={{
                textAlign: 'center',
                fontWeight: 600,
                color: titleColors.primary,
                mb: 1,
              }}
            >
              {titleData.name || titleData.newTitle}
            </Typography>

            {/* Title Description */}
            <Typography
              variant="body1"
              sx={{
                textAlign: 'center',
                color: isDark ? 'rgba(255, 255, 255, 0.8)' : 'rgba(0, 0, 0, 0.7)',
                mb: 2,
                lineHeight: 1.5,
              }}
            >
              {titleData.description || `You are now ${titleData.name || titleData.newTitle}!`}
            </Typography>

            {/* Achievement Stats */}
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center',
              alignItems: 'center',
              gap: 2,
              flexWrap: 'wrap'
            }}>
              {titleData.achievementCount && (
                <Box sx={{
                  px: 2,
                  py: 1,
                  borderRadius: '16px',
                  background: `${titleColors.primary}20`,
                  border: `1px solid ${titleColors.primary}30`,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}>
                  <Title sx={{ 
                    color: titleColors.primary,
                    fontSize: 18
                  }} />
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 'bold',
                      color: titleColors.primary,
                    }}
                  >
                    {titleData.achievementCount} Achievements
                  </Typography>
                </Box>
              )}
              
              {titleData.totalPoints && (
                <Box sx={{
                  px: 2,
                  py: 1,
                  borderRadius: '16px',
                  background: `${titleColors.primary}20`,
                  border: `1px solid ${titleColors.primary}30`,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}>
                  <AutoAwesome sx={{ 
                    color: titleColors.primary,
                    fontSize: 18
                  }} />
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 'bold',
                      color: titleColors.primary,
                    }}
                  >
                    {titleData.totalPoints} Points
                  </Typography>
                </Box>
              )}
            </Box>
            </motion.div>
          </SwipeableNotification>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// Utility function to trigger title notifications
export const triggerTitleNotification = (titleData) => {
  const event = new CustomEvent('titleUpdated', { detail: titleData });
  window.dispatchEvent(event);
};

export default PremiumTitleNotification;